import { useState } from 'react';
import { useAppConfig } from '../../../../../hooks/useAppConfig';
import type { PhotoResult } from '../../../../../hooks/usePhotoGallery';
import { usePhotoPicker } from '../../../../../hooks/usePhotoPicker';
import type { NewPostPhotoInput } from '../../../../../types/graphql-generated';
import type { EditorPhoto, PostPhoto } from './types';

// Post photos are shown full screen, so they are picked larger than avatars
// (600 px) while staying under the backend's 2 MB limit.
const POST_PHOTO_OPTIONS = { targetWidth: 1600, targetHeight: 1600 };

/** The photo fields of the post save: empty when the photos did not change. */
export type PhotoChangesInput = { photoIds?: string[]; newPhotos?: NewPostPhotoInput[] };

/**
 * Photo changes of the post editor. Adding and removing photos only changes the
 * editor's list. Nothing is uploaded until the post is saved: `toInput` gives the
 * photos to keep and the new ones, sent in the same request as the text, so the
 * text and the photos change together. Leaving without saving discards them.
 */
export const usePostPhotoChanges = () => {
  const { postMaxPhotos } = useAppConfig();
  const { pickFromCamera, pickFromGallery } = usePhotoPicker(POST_PHOTO_OPTIONS);
  const [photos, setPhotos] = useState<EditorPhoto[]>([]);
  // Photos the post shows on the server, to know whether the list changed
  const [savedIds, setSavedIds] = useState<string[]>([]);

  const ids = photos.map((photo) => photo.id);
  const hasChanges = ids.length !== savedIds.length || ids.some((id, index) => id !== savedIds[index]);

  /** Starts from the photos of the loaded post, discarding any change. */
  const reset = (saved: PostPhoto[]) => {
    setPhotos(saved.map(({ id, url }) => ({ key: id, id, url })));
    setSavedIds(saved.map(({ id }) => id));
  };

  // A new photo's key is also its upload key: a retried save sends the same one,
  // so the backend does not add the photo twice.
  const add = (upload: PhotoResult) => {
    const key = crypto.randomUUID();
    const url = `data:${upload.contentType};base64,${upload.base64}`;
    setPhotos((current) => (current.length >= postMaxPhotos ? current : [...current, { key, url, upload }]));
  };

  const remove = (key: string) => setPhotos((current) => current.filter((photo) => photo.key !== key));

  const toInput = (): PhotoChangesInput => {
    if (!hasChanges) return {};
    return {
      photoIds: photos.flatMap((photo) => (photo.id === undefined ? [] : [photo.id])),
      newPhotos: photos.flatMap(({ key, upload }) =>
        upload === undefined ? [] : [{ uploadKey: key, imageBase64: upload.base64, contentType: upload.contentType }]
      ),
    };
  };

  return {
    photos,
    hasChanges,
    reset,
    remove,
    addFromCamera: () => pickFromCamera(add),
    addFromGallery: () => pickFromGallery(add),
    toInput,
  };
};
